import { t } from '@lingui/core/macro';
import { PartCategoryDTO } from '@luminovo/api-contracts';
import { FieldSelect, FieldSelectProps, Flexbox, Text } from '@luminovo/design-system';
import React, { type JSX } from 'react';

/**
 * Hardcoded part category for off-the-shelf parts
 * We need this to be able to set the default value of the part category select
 */
export const PART_CATEGORY_OFF_THE_SHELF = {
    id: 1,
    name: `Off-the-shelf part`,
    children: [2, 101, 334, 345],
    depth: 0,
};

export function FieldPartCategory({
    onChange,
    ...props
}: Omit<FieldSelectProps<PartCategoryDTO>, 'onChange'> & { onChange: (value: PartCategoryDTO) => void }): JSX.Element {
    const { options } = props;

    const partCategoryMap = React.useMemo(() => {
        const childrenToParentMap = new Map(
            options.flatMap((category) => category.children.map((child) => [child, category])),
        );

        // Helper function to build the path for a given part category
        const buildPath = (category: PartCategoryDTO): PartCategoryDTO[] => {
            if (category.depth === 0) {
                return [category];
            }

            const parentCategory = childrenToParentMap.get(category.id);
            const parentPath = parentCategory ? buildPath(parentCategory) : [];

            return [...parentPath, category];
        };

        return new Map(
            options.map((item) => {
                const path = buildPath(item);
                if (path.length <= 3) {
                    return [item.id, path.map((category) => category.name).join(' / ')];
                } else {
                    return [
                        item.id,
                        `... / ${path
                            .slice(-3)
                            .map((category) => category.name)
                            .join(' / ')}`,
                    ];
                }
            }),
        );
    }, [options]);

    return (
        <FieldSelect
            disabled={props.options.length === 0}
            getOptionLabel={(option) => option.name}
            renderOption={(option) => {
                return (
                    <Flexbox
                        gap={4}
                        sx={{
                            flexDirection: 'column',
                            maxWidth: '100%',
                        }}
                    >
                        <Text variant={'body-small'}>{option.name}</Text>
                        <Text variant="caption" showEllipsis>
                            {partCategoryMap.get(option.id) ?? ''}
                        </Text>
                    </Flexbox>
                );
            }}
            placeholder={t`Part category`}
            disableClearable
            onChange={(partType) => onChange(partType ?? PART_CATEGORY_OFF_THE_SHELF)}
            {...props}
        />
    );
}
